import type { QuestionModule } from "../contracts";
import { rowSpecifyFields } from "../specify/rowFields";
import {
  gridMultiSelectCore,
  type GridMultiSelectDefinition,
  type GridMultiSelectDraft,
  type GridMultiSelectEvent,
  type GridMultiSelectRaw,
  type GridMultiSelectResponse,
} from "./core";
import {
  GridMultiSelectButtonsView,
  GridMultiSelectCheckboxView,
} from "./views";

export const gridMultiSelectModule: QuestionModule<
  GridMultiSelectRaw,
  GridMultiSelectDefinition,
  GridMultiSelectDraft,
  GridMultiSelectEvent,
  GridMultiSelectResponse
> = {
  type: "grid_multi_select",
  galleryFeatures: [
    "visual-groups",
    "row-custom-text",
    "baseline",
    "rows",
    "images",
    "row-images",
    "disabled",
    "exclusive",
    "custom-text",
    "restored",
  ],
  core: gridMultiSelectCore,
  prompt: (definition) => ({
    content: definition.prompt,
    image: definition.image,
    altText: definition.altText,
    error: definition.error,
  }),
  views: {
    buttons: {
      id: "buttons",
      label: "Buttons",
      component: GridMultiSelectButtonsView,
      space: "expanded",
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
    },
    checkbox: {
      id: "checkbox",
      label: "Checkboxes",
      component: GridMultiSelectCheckboxView,
      space: "expanded",
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
    },
  },
  defaultDisplay: { view: "buttons" },
  resolveDisplay: (raw) => ({
    view: raw.style === "button" ? "buttons" : raw.style,
  }),
  controls: (definition, draft, _display, copy) => ({
    specify: [
      ...rowSpecifyFields(
        definition.rows,
        draft.specifiedRows ?? {},
        (rowId, value): GridMultiSelectEvent => ({
          type: "set-row-label",
          rowId,
          value,
        }),
      ),
      ...definition.rows.flatMap((row) =>
        definition.options.flatMap((option) =>
          option.customPrompt === undefined
            ? []
            : [
                {
                  target: {
                    kind: "option-answer" as const,
                    rowId: row.id,
                    optionId: option.id,
                  },
                  prompt: option.customPrompt,
                  value: draft.customTextByRow[row.id] ?? "",
                  selected: Boolean(
                    draft.selectedByRow[row.id]?.includes(option.id),
                  ),
                  disabled: option.disabled,
                  confirm: (value: string): GridMultiSelectEvent => ({
                    type: "set-custom-answer",
                    rowId: row.id,
                    optionId: option.id,
                    value,
                  }),
                  remove: {
                    type: "toggle",
                    rowId: row.id,
                    optionId: option.id,
                  } as GridMultiSelectEvent,
                },
              ],
        ),
      ),
    ],
    primaryAction: {
      purpose: "submit",
      label: copy.actions.send,
      action: { type: "submit" },
    },
  }),
};
